import { Warning, TrendUp, TrendDown } from "@phosphor-icons/react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, BarChart, Cell, ReferenceLine } from "recharts";
import { money, num, pct, MONTHS_ES, SEMAFORO } from "./faUtils";

function Kpi({ label, value, sub, tone = "slate", testid }) {
  const styles = {
    slate:   "bg-white border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber:   "bg-amber-50 border-amber-200 text-amber-800",
    rose:    "bg-rose-50 border-rose-200 text-rose-800",
    sky:     "bg-sky-50 border-sky-200 text-sky-800",
    indigo:  "bg-indigo-50 border-indigo-200 text-indigo-800",
  }[tone];
  return (
    <div className={`border rounded-lg p-4 shadow-sm ${styles}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest font-bold opacity-70 mb-1">{label}</p>
      <p className="font-heading font-black text-xl leading-tight">{value}</p>
      {sub && <p className="text-[11px] opacity-70 mt-1">{sub}</p>}
    </div>
  );
}

const PARETO_COLORS = ["#1e3a8a", "#b45309", "#0f766e", "#9f1239", "#4338ca", "#a16207", "#334155", "#7c2d12", "#155e75"];

export default function FaResumen({ report }) {
  const tr = report.totals.real || {};
  const tb = report.totals.budget || {};
  const ytdR = report.ytd.real || {};
  const ytdB = report.ytd.budget || {};
  const hasReal = Object.keys(tr).length > 0;
  const cumpl = tb.ml_total > 0 ? (tr.ml_total || 0) / tb.ml_total * 100 : 0;
  const ebitdaTone = !hasReal ? "slate" : tr.margen_ebitda_pct < 25 ? "rose" : tr.margen_ebitda_pct <= 32 ? "amber" : "emerald";

  const chartData = report.months.map((m) => ({
    name: m.label,
    "ML Real": m.real_has_data ? m.real.ml_total : null,
    "ML Budget": m.budget_has_data ? m.budget.ml_total : null,
    "Ingreso": m.real_has_data ? m.real.ingreso_total : null,
    "EBITDA": m.real_has_data ? m.real.ebitda : null,
  }));

  return (
    <div className="space-y-5 pt-3">
      {/* KPIs anuales */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi label={`Ingreso ${report.year}`} value={money(tr.ingreso_total)} sub={`Budget ${money(tb.ingreso_total)}`} tone="sky" testid="fa-kpi-ingreso" />
        <Kpi label="EBITDA" value={money(tr.ebitda)} sub={`Margen ${pct(tr.margen_ebitda_pct)} · Meta >32%`} tone={ebitdaTone} testid="fa-kpi-ebitda" />
        <Kpi label="Utilidad Neta" value={money(tr.utilidad_neta)} sub={`Margen ${pct(tr.margen_neto_pct)}`} tone={Number(tr.utilidad_neta) >= 0 ? "emerald" : "rose"} testid="fa-kpi-un" />
        <Kpi label="FCF al Dueño" value={money(tr.fcfe)} sub={`FCF ${money(tr.fcf)}`} tone={Number(tr.fcfe) >= 0 ? "emerald" : "rose"} testid="fa-kpi-fcfe" />
        <Kpi label="Costo $/ML" value={money(tr.costo_por_ml, 0)} sub={`Budget ${money(tb.costo_por_ml, 0)} · BE ${num(tr.break_even_ml)} ML/mes`} tone="indigo" testid="fa-kpi-costoml" />
        <Kpi label="Cumplimiento ML" value={pct(cumpl)} sub={`${num(tr.ml_total)} / ${num(tb.ml_total)} ML`} tone={cumpl >= 100 ? "emerald" : cumpl >= 85 ? "amber" : "rose"} testid="fa-kpi-cumpl" />
      </div>

      {/* Alertas */}
      {report.alerts.length > 0 && (
        <div className="border border-red-200 bg-red-50/70 rounded-lg p-4 space-y-1.5" data-testid="fa-alerts">
          <p className="text-[10px] uppercase tracking-widest font-bold text-red-700 flex items-center gap-1.5">
            <Warning size={13} weight="fill" /> Alertas de caja y rentabilidad ({report.alerts.length})
          </p>
          {report.alerts.map((a, i) => (
            <p key={i} className={`text-xs flex items-start gap-1.5 ${a.level === "rojo" ? "text-red-900 font-semibold" : "text-amber-900"}`}>
              <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${a.level === "rojo" ? "bg-red-500" : "bg-amber-500"}`} />
              {a.text}
            </p>
          ))}
        </div>
      )}

      {/* Evolución mensual */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-3">Evolución ML · Ingreso · EBITDA</p>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="ml" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="money" orientation="right" tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(1)}M`} />
              <Tooltip formatter={(v, n) => n.startsWith("ML") ? num(v) : money(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="ml" dataKey="ML Budget" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
              <Bar yAxisId="ml" dataKey="ML Real" fill="#1e3a8a" radius={[3, 3, 0, 0]} />
              <Line yAxisId="money" dataKey="Ingreso" stroke="#0369a1" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              <Line yAxisId="money" dataKey="EBITDA" stroke="#b45309" strokeWidth={2} dot={{ r: 3 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Pareto costo por ML */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid="fa-pareto">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-3">
            Pareto de costo por ML {hasReal ? "(real)" : "(budget)"} — total {money((hasReal ? tr : tb).costo_por_ml, 0)}/ML
          </p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={report.pareto} layout="vertical" margin={{ left: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => money(v)} />
              <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v, n, p) => [`${money(v, 2)}/ML · ${p.payload.pct}% del costo`, p.payload.label]} />
              <Bar dataKey="por_ml" radius={[0, 4, 4, 0]}>
                {report.pareto.map((_, i) => <Cell key={i} fill={PARETO_COLORS[i % PARETO_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* YTD + Break-even + Ratios */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid="fa-ytd">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
            YTD (a {report.ytd.through_month > 0 ? MONTHS_ES[report.ytd.through_month - 1] : "—"})
          </p>
          {[["Ingreso", ytdR.ingreso_total, ytdB.ingreso_total], ["EBITDA", ytdR.ebitda, ytdB.ebitda],
            ["Utilidad Neta", ytdR.utilidad_neta, ytdB.utilidad_neta], ["ML", ytdR.ml_total, ytdB.ml_total, true]].map(([l, r, b, isML]) => {
            const d = (r || 0) - (b || 0);
            return (
              <div key={l} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0 text-sm">
                <span className="text-slate-500">{l}</span>
                <span className="font-semibold text-slate-900">{isML ? num(r) : money(r)}</span>
                <span className={`text-[11px] font-bold flex items-center gap-0.5 ${d >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {d >= 0 ? <TrendUp size={11} weight="bold" /> : <TrendDown size={11} weight="bold" />}
                  {isML ? num(d) : money(d)}
                </span>
              </div>
            );
          })}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid="fa-breakeven">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Punto de equilibrio (anual)</p>
          <p className="text-3xl font-heading font-black text-slate-900">{num(tr.break_even_ml || tb.break_even_ml)} <span className="text-sm font-bold text-slate-400">ML/mes</span></p>
          <p className="text-[11px] text-slate-500 mt-1">Ingreso equilibrio: {money(tr.break_even_ingreso || tb.break_even_ingreso)}</p>
          <p className="text-[11px] text-slate-500">Costo variable: {money(tr.cv_por_ml || tb.cv_por_ml, 0)}/ML · Precio prom: {money(tr.precio_ajustado_prom || tb.precio_ajustado_prom, 0)}/ML</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid="fa-ratios">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Ratios financieros</p>
          {[["Deuda / EBITDA", `${num(report.ratios.endeudamiento_deuda_ebitda, 2)}x`],
            ["Cobertura intereses", `${num(report.ratios.cobertura_intereses, 2)}x`],
            ["Deuda pendiente equipos", money(report.ratios.deuda_pendiente)],
            ["Margen EBITDA", pct(report.ratios.margen_ebitda_pct)]].map(([l, v]) => (
            <div key={l} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0 text-sm">
              <span className="text-slate-500">{l}</span><span className="font-semibold text-slate-900">{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Cumplimiento por frente */}
      {report.frentes_cumplimiento.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm overflow-x-auto" data-testid="fa-frentes-cumplimiento">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Cumplimiento por frente ({report.year})</p>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-200">
              <th className="py-1.5 pr-3">Frente</th><th className="pr-3">Sección</th><th className="pr-3">Terreno</th>
              <th className="pr-3 text-right">Precio ML</th><th className="pr-3 text-right">ML Budget</th>
              <th className="pr-3 text-right">ML Real</th><th className="text-right">Cumplimiento</th>
            </tr></thead>
            <tbody>
              {report.frentes_cumplimiento.map((f) => (
                <tr key={f.id} className="border-b border-slate-50">
                  <td className="py-1.5 pr-3 font-semibold text-slate-900">{f.name}</td>
                  <td className="pr-3 text-slate-500">{f.seccion}</td>
                  <td className="pr-3 text-slate-500">{f.terreno}</td>
                  <td className="pr-3 text-right">{money(f.precio_ml)}</td>
                  <td className="pr-3 text-right">{num(f.ml_budget)}</td>
                  <td className="pr-3 text-right font-semibold">{num(f.ml_real)}</td>
                  <td className={`text-right font-bold ${f.cumplimiento_pct >= 100 ? "text-emerald-700" : f.cumplimiento_pct >= 85 ? "text-amber-700" : "text-red-700"}`}>
                    {pct(f.cumplimiento_pct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Semáforo mensual */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid="fa-semaforo-mensual">
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-3">Semáforo EBITDA mensual · &lt;25% rojo · 25-32% amarillo · &gt;32% verde</p>
        <div className="grid grid-cols-6 md:grid-cols-12 gap-1.5">
          {report.months.map((m) => {
            const s = SEMAFORO[m.semaforo];
            return (
              <div key={m.month} className="text-center">
                <div className={`h-9 rounded flex items-center justify-center text-[10px] font-bold ${
                  m.semaforo === "rojo" ? "bg-red-500 text-white" : m.semaforo === "amarillo" ? "bg-amber-400 text-amber-950" :
                  m.semaforo === "verde" ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"}`}
                  title={`${m.label}: ${s.label}`}>
                  {m.real_has_data ? pct(m.real.margen_ebitda_pct) : "—"}
                </div>
                <p className="text-[9px] uppercase font-bold text-slate-400 mt-0.5">{m.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
